import styled, { css } from "styled-components";
import barsSolid from "../../assets/bars-solid.svg";
import penSolid from "../../assets/pen-solid.svg";
import chevronDownSolid from "../../assets/chevron-down-solid.svg";
import { theme } from "../../styles/theme";

const menuBackgroundColor = "white";
const hoverMenuItemBackgroundColor = "#f3953f";
const menuItemSpanColor = "black";

// Filters
const menuItemIconColor = "brightness(0) saturate(100%)";
const menuItemIconHoverColor =
  "brightness(0) saturate(100%) invert(60%) sepia(76%) saturate(526%) hue-rotate(341deg) brightness(98%) contrast(95%)";
const barsFilter =
  "brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(3447%) hue-rotate(4deg) brightness(77%) contrast(91%)";
const penFilter =
  "brightness(0) saturate(100%) invert(59%) sepia(83%) saturate(427%) hue-rotate(341deg) brightness(98%) contrast(95%)";

const headerHeight = "3rem";
const footerHeight = "2.5rem";
const contentHeight = `calc(98vh)`;

export const Container = styled.div`
  overflow: hidden;
`;

// Mobile toggle button (outside the aside)
export const MobileToggleButton = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media screen and (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 1.5rem;
    left: 1rem;
    z-index: 10000;
    cursor: pointer;
    background-color: white;
    border: 1px solid #e9e9e9;
    border-radius: 8px;
    width: 45px;
    height: 45px;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        left: 255px;
      `}

    &:active {
      transform: scale(0.95);
    }

    & svg {
      width: 25px;
      height: 25px;
      fill: #374151;
    }
  }
`;

export const HeaderContainer = styled.header`
  box-sizing: border-box;
  display: flex;
  height: ${headerHeight};
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  position: sticky;
  background-color: ${theme.colors.background};
  padding: 0.3rem 1rem;
  top: 0;
  transition: box-shadow 0.3s;
  width: 100%;
  z-index: 100;
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media screen and (max-width: 768px) {
    padding-left: 4rem;
  }
`;

export const HeaderLogo = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  height: 3rem;
  align-items: center;
  padding: 0 0.5rem;
  margin-bottom: 3rem;
  justify-content: space-between;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    width: calc(100% - 1rem);
  }

  & > .icon {
    transition: opacity 0.3s, width 0.3s;
    ${({ $isOpen }) =>
      !$isOpen &&
      css`
        opacity: 0;
        width: 0;
        overflow: hidden;
      `}
    @media screen and (max-width: 768px) {
      width: 100%;
    }
    & > svg {
      height: calc(${headerHeight} - 0.6rem);
      width: fit-content;
    }
  }
`;

export const AsideToggle = styled.div<{ $isOpen: boolean }>`
  cursor: pointer;
  height: 100%;
  position: relative;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 35px;

  @media screen and (max-width: 768px) {
    display: none;
  }

  &::before {
    background: url(${barsSolid}) no-repeat center center / 22px 22px;
    content: "";
    display: block;
    filter: ${barsFilter};
    height: 35px;
    width: 35px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition: background-size 0.08s, filter 0.3s, opacity 0.5s;
  }

  @media screen and (min-width: 769px) {
    &::before {
      filter: ${menuItemIconColor} !important;
      opacity: 0.5;
    }

    &:hover::before {
      background-size: 20px 20px;
    }
  }

  ${({ $isOpen }) =>
    !$isOpen &&
    css`
      margin: 0 auto;

      &::before {
        opacity: 1;
      }

      @media screen and (min-width: 769px) {
        &:hover::before {
          background-size: 22px 22px;
        }
      }
    `}
`;

export const HeaderHospitalActive = styled.div<{ $noEdit?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  background-color: white;
  border: 1px solid #e9e9e9;
  border-radius: 6px;
  padding: 0.75rem 1rem;

  @media screen and (max-width: 768px) {
    position: static;
    transform: none;
    margin-left: auto;
  }

  ${({ $noEdit }) =>
    !$noEdit &&
    css`
      pointer-events: all;
      & > span {
        cursor: pointer;
        position: relative;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        &:hover {
          color: #f3953f;
        }
        & .icon {
          display: flex;
          align-items: center;
          transition: transform 0.2s;
          & svg {
            transition: filter 0.2s;
          }
        }
        &:hover .icon svg {
          transform: scale(1.1);
          filter: brightness(0) saturate(100%) invert(60%) sepia(76%) saturate(526%) hue-rotate(341deg) brightness(98%) contrast(95%);
        }
      }
    `}

  & > span {
    font-weight: 600;
    font-size: 14px;
    color: #374151;
    text-transform: none;
    white-space: nowrap;
  }
`;

export const HeaderRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
`;

export const HeaderUser = styled.div`
  display: flex;
  gap: 0.5rem;

  @media screen and (max-width: 500px) {
    display: none;
  }

  & .header_user_content {
    line-height: 1.1;
    align-content: center;
    font-weight: 600;
    font-size: 13px;
    &_hospital,
    &_name {
      text-align: start;
    }
  }

  & .header_user_role {
    font-weight: 400;
  }

  & .header_user_image {
    align-content: center;
    & > svg {
      width: 35px;
    }
  }
`;

export const AsideContainer = styled.aside<{ $isOpen: boolean }>`
  background-color: ${menuBackgroundColor};
  box-sizing: border-box;
  height: ${contentHeight};
  border: 1px solid #e9e9e9;
  position: fixed;
  transition: width 0.3s, transform 0.3s;
  width: 60px;
  z-index: 10001;
  padding-top: 1rem;
  border-radius: 0 1rem 1rem 0;
  display: flex;
  flex-direction: column;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          width: 250px;
          & > ul > li > a {
            justify-content: center;
            align-items: center;
            padding: 0.2rem 1rem !important;
          }
          & ul > li > a > span {
            display: block;
            white-space: nowrap;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          & > ul > li.--is_dropdown > ul {
            background-color: ${menuBackgroundColor};
            padding-left: 10px;
            color: ${menuItemSpanColor};
            position: relative !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            & > li > a {
              justify-content: flex-start !important;
              padding-left: 2rem !important;
            }
          }
        `
      : css`
          & > ul > li > a {
            justify-content: center !important;
            padding: 0.2rem 0 !important;
          }
          & > ul > li.--is_dropdown {
            position: relative;
            & > a > span::after {
              display: none !important;
            }
            & > ul {
              position: absolute !important;
              left: 60px !important;
              top: 0 !important;
              width: 200px !important;
              height: auto !important;
              max-height: 80vh !important;
              overflow-y: auto !important;
              display: none !important;
              box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2) !important;
              background-color: ${menuBackgroundColor} !important;
              border-radius: 0 0.5rem 0.5rem 0 !important;
              z-index: 102 !important;
              transition: none !important;
              & > li > a {
                justify-content: flex-start !important;
                padding-left: 1rem !important;
              }
            }
            &:hover > ul {
              display: block !important;
            }
          }
          & > ul > li > a > span {
            @media screen and (min-width: 768px) {
              display: none;
            }
          }
        `}

  @media screen and (max-width: 768px) {
    width: 250px;
    transform: translateX(-100%);
    
    ${({ $isOpen }) =>
      $isOpen &&
      css`
        transform: translateX(0);
      `}
  }

  & > ul {
    display: flex;
    flex-direction: column;
    list-style: none;
    overflow-y: auto;
    min-height: 0;

    &:first-of-type {
      flex: 1;
    }

    @media screen and (max-width: 768px) {
      overflow: auto;
      &:first-of-type {
        flex: none;
        height: 50vh;
      }
    }

    & > li {
      align-content: center;
      background-color: ${menuBackgroundColor};
      color: ${menuItemSpanColor};
      transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-left 0.2s ease-in-out;

      & input {
        background-color: transparent;
        width: 100%;
        outline: none;
        padding: 0.5rem 0.8rem;
        color: ${menuItemSpanColor} !important;
        &::placeholder {
          color: ${menuItemSpanColor} !important;
          opacity: 0.4;
        }
      }

      & > ul {
        height: 0px;
        max-height: fit-content;
        overflow: hidden;
        transition: height 0.8s;
      }

      &:hover > ul,
      & > ul:has(input:focus, .--active) {
        @media screen and (min-width: 768px) {
          ${({ $isOpen }: any) =>
            $isOpen &&
            css`
              height: auto;
              max-height: 80vh;
              overflow-y: auto;
            `}
        }
        @media screen and (max-width: 768px) {
          height: auto;
          max-height: 80vh;
          overflow-y: auto;
        }
      }

      &.--change_hospital {
        @media screen and (min-width: 800px) {
          display: none;
        }
      }

      &.--custom_placeholder {
        display: block;
      }

      &.--logout_bottom {
        font-weight: bold;
      }

      &.--hidden {
        display: none;
      }

      &.--from_awesome > a > i > svg {
        height: 20px;
      }

      &:hover:not(:has(ul:hover)),
      &:has(> a:hover),
      &:has(> ul input:focus),
      &.--active,
      &:has(.--active) {
        color: ${hoverMenuItemBackgroundColor};
        &:not(:has(> input)) {
          background-color: rgba(243, 149, 63, 0.15);
          border-left: 0.35rem solid ${hoverMenuItemBackgroundColor};
        }
        & > a {
          opacity: 1;
          & > i {
            filter: ${menuItemIconHoverColor};
          }
        }
      }

      &.--is_dropdown:hover:not(:has(ul:hover)) {
        color: ${hoverMenuItemBackgroundColor};
        background-color: rgba(243, 149, 63, 0.15);
      }

      &.--is_dropdown > a > span::after {
        content: "";
        display: block;
        background: url(${chevronDownSolid}) no-repeat center center / 10px 10px;
        width: 10px;
        height: 10px;
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        transition: top 0.3s;
      }

      & > a {
        align-items: center;
        display: flex;
        gap: 0.5rem;
        height: 50px;
        opacity: 0.6;
        padding: 0.2rem 0.2rem 0.2rem 1rem;

        & > span {
          transition: color 0.3s;
          white-space: nowrap;
          width: 100%;
          position: relative;
        }

        & > i {
          align-content: center;
          transition: none;
          & > svg {
            filter: ${menuItemIconColor};
            max-height: 25px;
            width: 25px;
            transition: filter 0s;
          }
        }
      }
    }
  }
`;

export const Content = styled.div<{ $isOpen: boolean }>`
  height: ${contentHeight};
  overflow: auto;
  margin-left: 80px;
  transition: margin-left 0.3s;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      margin-left: 280px;
    `}

  @media screen and (max-width: 768px) {
    margin-left: 0px;
  }
`;

export const FooterContainer = styled.footer`
  align-items: center;
  background-color: ${menuBackgroundColor};
  bottom: 0;
  box-shadow: 0 -1px 4px 1px rgba(0, 0, 0, 0.2);
  display: flex;
  height: ${footerHeight};
  justify-content: right;
  position: sticky;
  width: 100%;
  z-index: 101;

  @media screen and (max-width: 800px) {
    justify-content: center;
  }

  @media screen and (max-width: 500px) {
    font-size: 13px;
  }
`;

export const FooterHospitalActive = styled.div<{ $noEdit?: boolean }>`
  align-content: center;
  display: none;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0.2rem;
  width: 100%;
  text-align: center;

  ${({ $noEdit }) =>
    !$noEdit &&
    css`
      pointer-events: all;
      & > span {
        cursor: pointer;
        position: relative;
        transition: text-shadow 0.3s;
        &:hover {
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
        }
        &::after {
          background: url(${penSolid}) no-repeat center center / cover;
          content: "";
          display: block;
          filter: ${penFilter};
          height: 12px;
          position: absolute;
          right: -1rem;
          top: calc(50% - 0.2rem);
          transform: translateY(-50%);
          width: 12px;
        }
      }
    `}

  @media screen and (max-width: 800px) {
    display: block;
  }

  & > span {
    font-weight: 600;
    font-size: 14.5px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.4);
    text-transform: uppercase;
  }
`;

export const Datetime = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 1rem;
  line-height: 1.2;
  margin-bottom: 1rem;

  .time {
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
  }

  .date {
    font-size: 0.9rem;
    color: #666;
    text-transform: capitalize;
  }
`;